import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, CloudDownload, Storage } from '@mui/icons-material';
import { Header } from '../components/layout/Header';
import { actualDataService, ActualDataSource } from '../services/actualDataService';
import { format } from 'date-fns';

export const ActualDataSourcesPage: React.FC = () => {
  const [dataSources, setDataSources] = useState<ActualDataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<ActualDataSource | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [cacheDialogOpen, setCacheDialogOpen] = useState(false);
  const [cacheLoading, setCacheLoading] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [jdbcUrl, setJdbcUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = async () => {
    try {
      setLoading(true);
      const data = await actualDataService.getDataSources();
      setDataSources(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Nem sikerült betölteni az adatforrásokat');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (source?: ActualDataSource) => {
    if (source) {
      setEditingSource(source);
      setName(source.name);
      setDescription(source.description || '');
      setJdbcUrl(source.jdbcUrl);
      setUsername(source.username);
      setPassword(''); // Don't show password
    } else {
      setEditingSource(null);
      setName('');
      setDescription('');
      setJdbcUrl('jdbc:postgresql://indecs-db.postgres.database.azure.com:5432/indecs_db?sslmode=require');
      setUsername('t6yzq76o');
      setPassword('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSource(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSave = async () => {
    if (!name.trim() || !jdbcUrl.trim() || !username.trim()) {
      setError('Név, JDBC URL és felhasználónév kötelező');
      return;
    }

    if (!editingSource && !password.trim()) {
      setError('Jelszó kötelező új adatforrás létrehozásához');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (editingSource) {
        const updates: any = { name, description, jdbcUrl, username };
        if (password.trim()) {
          updates.password = password;
        }
        await actualDataService.updateDataSource(editingSource.id, updates);
        showSnackbar('Adatforrás sikeresen frissítve!', 'success');
      } else {
        await actualDataService.createDataSource({
          name,
          description,
          jdbcUrl,
          username,
          password,
        });
        showSnackbar('Adatforrás sikeresen létrehozva!', 'success');
      }

      await loadDataSources();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Nem sikerült menteni az adatforrást');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Biztosan törlöd ezt az adatforrást?')) return;

    try {
      await actualDataService.deleteDataSource(id);
      showSnackbar('Adatforrás sikeresen törölve!', 'success');
      await loadDataSources();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Nem sikerült törölni az adatforrást');
    }
  };

  const handleTest = async (id: number) => {
    try {
      setTesting(id);
      const result = await actualDataService.testConnection(id);
      if (result.valid) {
        showSnackbar('Kapcsolat rendben!', 'success');
      } else {
        showSnackbar('Nem sikerült kapcsolódni!', 'error');
      }
    } catch (err: any) {
      showSnackbar('Kapcsolat teszt sikertelen: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setTesting(null);
    }
  };

  const handlePopulateCache = async (id: number) => {
    setCacheLoading(true);
    setCacheDialogOpen(true);
    try {
      await actualDataService.populateCache(id);
      showSnackbar('Cache sikeresen betöltve!', 'success');
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Nem sikerült betölteni a cache-t', 'error');
    } finally {
      setCacheLoading(false);
      setCacheDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Storage /> Tényadat Források
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Új Adatforrás
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Név</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Leírás</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Adatbázis</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Létrehozva</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Műveletek</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataSources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Nincs még adatforrás. Hozz létre egyet!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dataSources.map((source) => (
                    <TableRow key={source.id} hover>
                      <TableCell>{source.name}</TableCell>
                      <TableCell>{source.description || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {source.username}@{new URL(source.jdbcUrl.replace('jdbc:', '')).hostname}
                        </Typography>
                      </TableCell>
                      <TableCell>{format(new Date(source.createdAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleTest(source.id)}
                            disabled={testing === source.id}
                            startIcon={testing === source.id ? <CircularProgress size={16} /> : <CheckCircle />}
                          >
                            Teszt
                          </Button>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(source)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(source.id)}
                          >
                            <Delete />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handlePopulateCache(source.id)}
                            title="Cache betöltése"
                          >
                            <CloudDownload />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSource ? 'Adatforrás Szerkesztése' : 'Új Adatforrás'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Név"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Leírás"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="JDBC URL"
                value={jdbcUrl}
                onChange={(e) => setJdbcUrl(e.target.value)}
                fullWidth
                required
                placeholder="jdbc:postgresql://host:port/database?params"
              />
              <TextField
                label="Felhasználónév"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Jelszó"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required={!editingSource}
                helperText={editingSource ? 'Hagyd üresen, ha nem változtatsz rajta' : ''}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Mégse</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? 'Mentés...' : 'Mentés'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cache Loading Dialog */}
        <Dialog
          open={cacheDialogOpen}
          maxWidth="xs"
          fullWidth
          onClose={() => {
            // Prevent closing if loading
            if (cacheLoading) return;
            setCacheDialogOpen(false);
          }}
          disableEscapeKeyDown={cacheLoading}
        >
          <DialogTitle>Cache betöltése</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Cache betöltése folyamatban... (Több percet is ignybe vehet.)</Typography>
          </DialogContent>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};
